/**
 * RegionAccessPicker — the one picker for attaching a person to regions.
 *
 * Top-level regions only, by construction: access to a region covers
 * everything inside it (BON-449), so there is nothing finer to pick and the
 * backend rejects a subregion outright. Each row names what it covers so the
 * breadth of the grant is visible without being selectable.
 *
 * Replaces the checkbox grid in the users screens and the checkbox tree on the
 * staff profile, which disagreed about whether subregions were pickable.
 */
import { ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

import RegionAccessList from './RegionAccessList';
import type { RegionOption } from './regionAccessOptions';

interface Props {
  options: RegionOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /** Region ids the viewer can't modify (outside their own scope). They render
   *  locked: visible but non-interactive, with a shield icon. */
  lockedIds?: string[];
  /** Allow one region at a time: picking another replaces the current one. */
  single?: boolean;
  /** 'inline' is an always-visible list (forms); 'popover' is a summarizing
   *  trigger that opens the same list (dense profile panels). */
  presentation?: 'inline' | 'popover';
  /** Popover trigger text when nothing is selected. */
  placeholder?: string;
  className?: string;
}

function triggerLabel(options: RegionOption[], selectedIds: string[], placeholder: string): string {
  if (selectedIds.length === 0) return placeholder;
  if (selectedIds.length === 1) {
    return options.find((o) => o.id === selectedIds[0])?.name ?? '1 region selected';
  }
  return `${selectedIds.length} regions selected`;
}

export default function RegionAccessPicker({
  options,
  selectedIds,
  onChange,
  lockedIds = [],
  single = false,
  presentation = 'inline',
  placeholder = 'Select regions...',
  className = '',
}: Props) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const list = (
    <RegionAccessList
      options={options}
      selectedIds={selectedIds}
      onChange={onChange}
      lockedIds={lockedIds}
      single={single}
    />
  );

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (presentation === 'inline') {
    return (
      <div
        className={`p-2.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-[#1e2520] ${className}`}
      >
        {list}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between gap-2 w-full px-3 py-1.5 text-sm rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] text-left"
      >
        <span
          className={
            selectedIds.length > 0
              ? 'text-bark-900 dark:text-stone-100'
              : 'text-stone-400 dark:text-stone-500'
          }
        >
          {triggerLabel(options, selectedIds, placeholder)}
        </span>
        <ChevronDown
          size={14}
          className={`flex-shrink-0 text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-52 max-h-64 overflow-y-auto rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-[#1e2521] shadow-lg p-1.5">
          {list}
        </div>
      )}
    </div>
  );
}
